import React from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  LayoutDashboard,
  Search,
  Briefcase,
  Inbox,
  User,
  Settings,
  LogOut,
  Languages,
  FileCheck,
  FilePlus,
  Award,
  Mic,
  TrendingUp,
  Bell,
  Crown,
  Target,
  Landmark,
  Compass,
  GraduationCap,
  Bell as BellIcon,
  ShieldCheck,
  LineChart,
  Sparkles,
} from "lucide-react";
import CodySupport from "@/components/CodySupport";

const navItems = [
  { to: "/app/profile", icon: User, key: "profile" },
  { to: "/app/dashboard", icon: LayoutDashboard, key: "dashboard" },
  // Recruiter-only — shown ABOVE regular search when user has a recruiter
  // or agency account (or an explicit employer_pro_override).
  { to: "/recruiter/search", icon: ShieldCheck, key: "recruiter_search", external: true, label: "🔍 Recruiter search engine", recruiterOnly: true },
  { to: "/app/search", icon: Search, key: "search" },
  { to: "/app/applications", icon: Briefcase, key: "applications" },
  { to: "/app/inbox", icon: Inbox, key: "inbox" },
  { to: "/app/alerts", icon: Bell, key: "alerts" },
  { to: "/app/weekly-brief", icon: BellIcon, key: "weekly_brief" },
  { to: "/app/cv-builder", icon: FilePlus, key: "cv_builder" },
  { to: "/app/cv-review", icon: FileCheck, key: "cv_review" },
  { to: "/app/certificates", icon: GraduationCap, key: "certificates" },
  { to: "/app/skills", icon: Award, key: "skills_nav" },
  { to: "/app/mock-interview", icon: Mic, key: "mock_interview" },
  { to: "/app/salary", icon: TrendingUp, key: "salary" },
  { to: "/become-premium", icon: Sparkles, key: "premium_nav", external: true, label: "⭐ Become Premium" },
  { to: "/app/billing", icon: Crown, key: "billing" },
  { to: "/app/privacy", icon: ShieldCheck, key: "privacy_nav" },
  { to: "/app/admin", icon: LineChart, key: "admin" },
  { to: "/app/settings", icon: Settings, key: "settings" },
];

// Dedicated sidebar for investor accounts.
// Investors never see candidate (CV Builder, Job Alerts, Skills) or recruiter
// (Candidate pool, Upload workers) tools. They get the deal-flow feed and
// account management entries only.
const investorNavItems = [
  { to: "/app/investor-feed", icon: Sparkles,    key: "inv_feed",     label: "✨ Investor feed" },
  { to: "/app/profile",       icon: User,        key: "inv_profile",  label: "Profile" },
  { to: "/app/inbox",         icon: Inbox,       key: "inv_inbox",    label: "Inbox" },
  { to: "/app/billing",       icon: Crown,       key: "inv_billing",  label: "Billing" },
  { to: "/app/settings",      icon: Settings,    key: "inv_settings", label: "Settings" },
];

// Dedicated sidebar for recruiter / agency accounts.
// They never see candidate-side tools (CV Builder, Job Alerts, Weekly Brief,
// Skills, Mock Interview, Salary Intel, Become Premium). Instead they get
// recruiter-relevant entries surfaced at the top of the nav.
const recruiterNavItems = [
  { to: "/app/profile",                    icon: User,           key: "profile",          label: "Profile" },
  { to: "/app/premium-members",            icon: Sparkles,       key: "rec_premium_mems", label: "✨ Premium members" },
  { to: "/recruiter/search",               icon: ShieldCheck,    key: "rec_search",       external: true, label: "🔍 Candidate pool" },
  { to: "/agencies/dashboard",             icon: FilePlus,       key: "rec_upload",       external: true, label: "📥 Upload workers" },
  { to: "/app/inbox",                      icon: Inbox,          key: "inbox",            label: "Inbox" },
  { to: "/app/billing",                    icon: Crown,          key: "billing",          label: "Billing" },
  { to: "/app/settings",                   icon: Settings,       key: "settings",         label: "Settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const isMockOnly = user?.feature_lock === "mock_interview_only";
  const isInvestor = user?.role === "investor";
  // Employer side = recruiter role, agency role, or explicit override
  const hasEmployerAccess = !!user && (
    user.role === "recruiter" ||
    user.role === "agency" ||
    user.employer_pro_override === true ||
    (user.subscription || "").toLowerCase() === "employer_pro"
  );

  // Hard-redirect locked users away from any path that isn't /app/mock-interview.
  if (isMockOnly && !location.pathname.startsWith("/app/mock-interview")) {
    return <Navigate to="/app/mock-interview" replace />;
  }

  // Investors never see the candidate Dashboard — push them to the feed.
  if (isInvestor && location.pathname === "/app/dashboard") {
    return <Navigate to="/app/investor-feed" replace />;
  }

  // Recruiter/agency accounts should never see the candidate Dashboard
  // (applications streak, first-application stats, etc.) — push them to
  // the Premium Members view, which is their natural home.
  if (hasEmployerAccess && location.pathname === "/app/dashboard") {
    return <Navigate to="/app/premium-members" replace />;
  }

  // Filter sidebar:
  //   - Mock-only locked users → only Mock Interview entry
  //   - Investor accounts → isolated investor sidebar (feed + account only)
  //   - Recruiter / agency / employer accounts → dedicated recruiter sidebar
  //     (never see candidate tools like CV Builder, Job Alerts, Skills, etc.)
  //   - Everyone else → full candidate nav minus recruiter-only entries
  let visibleNavItems;
  if (isMockOnly) {
    visibleNavItems = navItems.filter((it) => it.key === "mock_interview");
  } else if (isInvestor) {
    visibleNavItems = investorNavItems;
  } else if (hasEmployerAccess) {
    visibleNavItems = recruiterNavItems;
  } else {
    visibleNavItems = navItems.filter((it) => !it.recruiterOnly);
  }

  const onLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-[#1a1a1c] text-white">
      <aside
        className="hidden md:flex w-64 flex-col border-e border-white/10 bg-[#141416] sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/"
              className="flex items-center gap-1 group"
              title="Back to homepage"
              data-testid="sidebar-logo-home"
            >
              <span className="font-display font-black text-2xl tracking-tighter text-white inline-flex items-center group-hover:opacity-80 transition-opacity">
                <span className="border border-white px-1.5 py-0.5 mr-[2px] leading-none">M</span><span>aqam</span>
              </span>
            </Link>
            <button
              onClick={onLogout}
              title={t("logout")}
              aria-label={t("logout")}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:bg-white hover:text-[#1a1a1c] transition-colors"
              data-testid="logout-btn-top"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">Signed in as</div>
            <div className="font-display font-bold text-sm truncate text-white">{user?.full_name}</div>
            <div className="font-mono text-xs text-white/70 truncate" data-testid="sidebar-platform-email">
              {user?.platform_email}
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors rounded ${
                  isActive ? "bg-white text-[#1a1a1c]" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
              data-testid={`nav-${it.key}`}
            >
              <it.icon className="w-4 h-4 stroke-[1.75]" />
              {it.label || t(it.key)}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <button
            onClick={toggle}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors rounded"
            data-testid="lang-toggle-sidebar"
          >
            <Languages className="w-4 h-4" />
            {lang === "en" ? "العربية" : "English"}
          </button>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-white/70 hover:bg-white hover:text-[#1a1a1c] transition-colors rounded"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {t("logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 border-b border-white/10 bg-[#1a1a1c]/95 backdrop-blur px-4 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-1"
            title="Back to homepage"
            data-testid="mobile-logo-home"
          >
            <span className="font-display font-black text-lg tracking-tighter text-white inline-flex items-center">
              <span className="border border-white px-1 py-0.5 mr-[2px] leading-none">M</span><span>aqam</span>
            </span>
          </Link>
          <button
            onClick={onLogout}
            className="px-4 py-2 bg-white text-[#1a1a1c] text-sm font-bold flex items-center gap-2 rounded-full"
            data-testid="logout-mobile"
          >
            <LogOut className="w-4 h-4" />
            {t("logout")}
          </button>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden flex overflow-x-auto gap-2 p-3 border-b border-white/10 bg-[#141416]">
          {visibleNavItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-xs font-medium whitespace-nowrap rounded ${
                  isActive ? "bg-white text-[#1a1a1c]" : "bg-white/5 text-white/70 border border-white/10"
                }`
              }
            >
              <it.icon className="w-3.5 h-3.5" />
              {t(it.key)}
            </NavLink>
          ))}
        </div>

        <div className="dashboard-dark p-6 md:p-10 max-w-[1400px]">
          <Outlet />
        </div>

        <footer
          className="mt-auto border-t border-white/10 px-6 md:px-10 py-5 text-[11px] font-mono uppercase tracking-widest text-white/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-2"
          data-testid="app-copyright-footer"
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>© 2026 Maqam™ · Riyadh, KSA.</span>
            <span className="text-white/30">
              U.S. Copyright Office filing{" "}
              <span className="text-white/60">#1-15151075481</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <NavLink to="/copyright" className="hover:text-white transition-colors" data-testid="footer-copyright-link">
              Copyright
            </NavLink>
            <NavLink to="/privacy" className="hover:text-white transition-colors">
              Privacy
            </NavLink>
            <NavLink to="/terms" className="hover:text-white transition-colors">
              Terms
            </NavLink>
          </div>
        </footer>
      </main>

      {/* Cody — in-app AI support concierge. Hidden for mock-only locked users. */}
      {!isMockOnly && <CodySupport />}
    </div>
  );
}
